import { ArrowUpRight, MapPin, MapPinned } from "lucide-react";

import { LuxuryVisionBackdrop } from "@/components/site/LuxuryVisionBackdrop";
import { Button } from "@/components/ui/button";
import { bcCoverageLanguage } from "@/config/bc-coverage";
import { confirmedServiceAreas, type ServiceArea } from "@/config/service-areas";
import { localizeServiceArea, type PageUi } from "@/i18n/content";
import type { LocaleCode } from "@/i18n/routing";

export type LocalizedPathBuilder = (canonicalPath: string) => string;

type ServiceAreasIndexPageProps = {
  areas?: readonly ServiceArea[];
  buildPath?: LocalizedPathBuilder;
  contactPath?: string;
  locale?: LocaleCode;
  ui?: PageUi | undefined;
};

const identityPath: LocalizedPathBuilder = (path) => path;

export function ServiceAreasIndexPage({
  areas = confirmedServiceAreas,
  buildPath = identityPath,
  contactPath = "/contact",
  locale = "en-ca",
  ui,
}: ServiceAreasIndexPageProps) {
  const visibleAreas = areas
    .filter((area) => area.confirmed)
    .map((area) => localizeServiceArea(area, locale));
  const copy = ui?.serviceArea;
  const indexCopy = ui?.areaIndex;

  return (
    <>
      <section className="relative isolate overflow-hidden bg-slate-deep pt-32 pb-16 text-primary-foreground md:pt-40 md:pb-24">
        <LuxuryVisionBackdrop rotationKey="service-areas:index" priority />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[linear-gradient(105deg,oklch(0.16_0.06_255/0.97)_0%,oklch(0.16_0.06_255/0.84)_55%,oklch(0.16_0.06_255/0.46)_100%)]"
        />
        <div className="container-page">
          <nav
            aria-label={
              indexCopy?.breadcrumbLabel ?? ui?.serviceDetail.breadcrumbLabel ?? "Breadcrumb"
            }
            className="text-xs text-primary-foreground/55"
          >
            <a href={buildPath("/")} className="underline-offset-4 hover:underline">
              {ui?.common.home ?? "Home"}
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span aria-current="page">{ui?.common.serviceAreas ?? "Service areas"}</span>
          </nav>

          <div className="mt-10 max-w-3xl">
            <p className="eyebrow">
              {indexCopy?.eyebrow ?? (
                <>
                  {copy?.regions.metroVancouver ?? "Metro Vancouver"} &amp;{" "}
                  {copy?.regions.lowerMainland ?? "the Lower Mainland"}
                </>
              )}
            </p>
            <h1 className="display-1 mt-5 text-balance">
              {indexCopy?.title ?? ui?.common.serviceAreas ?? "Roofing service areas"}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/72 md:text-lg">
              {indexCopy?.body ??
                copy?.planningDisclaimer ??
                "Revolin Roofing assesses residential roofs across the communities below. Each project is planned for the property's roof form, exposure, access and applicable local requirements."}
            </p>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-page">
          <a
            href={buildPath("/service-areas/british-columbia")}
            className="group mb-10 flex flex-col justify-between gap-6 rounded-lg border border-border bg-slate-deep p-7 text-primary-foreground transition-colors hover:bg-slate-deep/92 focus-visible:bg-slate-deep/92 md:flex-row md:items-end md:p-9"
          >
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <MapPinned className="size-5 text-accent" aria-hidden="true" />
                <p className="eyebrow text-gold">British Columbia</p>
              </div>
              <h2 className="display-2 mt-4 text-balance">
                Scheduled roofing projects throughout BC
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-primary-foreground/72 md:text-base">
                {bcCoverageLanguage.planned}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-primary-foreground/60">
                {bcCoverageLanguage.serviceAreaDisclaimer}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-gold">
              View the British Columbia coverage page
              <ArrowUpRight
                className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
            </span>
          </a>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.68fr)_minmax(0,1.32fr)]">
            <div>
              <p className="eyebrow">
                {indexCopy?.coverageEyebrow ?? copy?.localContext ?? "Coverage, not branch offices"}
              </p>
              <h2 className="display-2 mt-3 text-balance">
                {indexCopy?.coverageTitle ??
                  copy?.propertyDecides ??
                  "Local context without fake locations"}
              </h2>
              <p className="mt-5 max-w-md leading-relaxed text-muted-foreground">
                {indexCopy?.coverageBody ??
                  copy?.confirmedCommunity ??
                  "These pages describe where roofing assessments may be available. They do not claim that Revolin Roofing maintains an office in every listed city. Contact us with the property address so coverage can be confirmed for the project."}
              </p>
              <Button variant="brand" size="xl" asChild className="mt-8">
                <a href={buildPath(contactPath)}>
                  {ui?.common.requestAssessment ?? "Request a Roof Assessment"}
                </a>
              </Button>
            </div>

            <ul className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2">
              {visibleAreas.map((area) => (
                <li key={area.slug} className="group bg-card">
                  <a
                    href={buildPath(`/service-areas/${area.slug}`)}
                    className="flex h-full min-h-56 flex-col p-7 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <MapPin className="size-5 text-accent" aria-hidden="true" />
                      <ArrowUpRight
                        className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        aria-hidden="true"
                      />
                    </div>
                    <p className="mt-8 text-xs tracking-[0.12em] uppercase text-muted-foreground">
                      {area.region === "Metro Vancouver"
                        ? (copy?.regions.metroVancouver ?? area.region)
                        : (copy?.regions.lowerMainland ?? area.region)}
                    </p>
                    <h2 className="display-3 mt-2 text-foreground">{area.name}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {area.intro}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-muted/50 py-12">
        <div className="container-page flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="eyebrow">
              {indexCopy?.nearbyEyebrow ?? ui?.common.serviceAreas ?? "Nearby community?"}
            </p>
            <h2 className="display-3 mt-2">
              {indexCopy?.nearbyTitle ??
                copy?.planningDisclaimer ??
                "Ask us to confirm project coverage."}
            </h2>
          </div>
          <Button variant="quiet" size="xl" asChild>
            <a href={buildPath(contactPath)}>
              {indexCopy?.contactCta ?? ui?.corePages.contact.eyebrow ?? "Contact Revolin Roofing"}
            </a>
          </Button>
        </div>
      </section>
    </>
  );
}
